import React, { useState, useEffect, useRef } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { formatCredits } from '../../../utils/numberFormat';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
  Pagination,
  IconButton,
  CircularProgress
} from '@mui/material';
import { Skeleton } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  AccountBalance,
  Add,
  Remove,
  ShoppingCart,
  History,
  Star,
  CheckCircle,
  Info,
  TrendingUp,
  Receipt,
  CreditCard,
  Close,
  Celebration,
  Assignment,
  Cancel
} from '@mui/icons-material';
import merchantAPI, { merchantAuthAPI } from '../../../services/merchantAPI';
import { getMerchantPlans, getMerchantAddons, getMerchantSubscription, cancelAutopay } from '../../../services/subscriptionAPI';
import SubscriptionCheckoutDialog from '../../subscription_v2/shared/SubscriptionCheckoutDialog';
import { useMerchantAuth } from '../../../contexts/MerchantAuthContext';
import DynamicMerchantTabs from '../Layout/DynamicMerchantTabs';
import { masterDataConfigAPI } from '../../../services/api';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MerchantWelcomeSection from '../Layout/MerchantWelcomeSection';
import useRouteMeta from '../../../hooks/useRouteMeta';
import FlutterServicesAPI from '../../../services/flutterServices';
const TXN_TYPE_LABELS = {
  SUBSCRIPTION_START:      'Subscription Start',
  SUBSCRIPTION_RENEWAL:    'Subscription Renewal',
  SUBSCRIPTION_UPGRADE:    'Subscription Upgrade',
  SUBSCRIPTION_DOWNGRADE:  'Subscription Downgrade',
  SUBSCRIPTION_CANCELLED:  'Subscription Cancelled',
  CREDIT_CARRY_FORWARD:    'Token Carry Forward',
  TOKEN_TOPUP:             'Token Pack Purchase',
  COUPON_BONUS:            'Coupon Bonus',
  CREDIT:                  'Token Added',
  DEBIT:                   'Tokens Used',
  ADMIN_ADJUSTMENT:        'Admin Adjustment',
  ADMIN_CREDIT_ADJUSTMENT: 'Admin Token Adjustment',
  REFUND:                  'Token Refund',
};

const getTypeLabel = (type) => {
  if (!type) return 'Unknown';
  return TXN_TYPE_LABELS[type] ||
    type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const MODULE_DISPLAY_NAMES = {
  'Catalog Management': 'Web Catalog',
};

const CreditsManagement = () => {
  const [searchParams] = useSearchParams();
  const [mobileCreditsOpen, setMobileCreditsOpen] = useState(() => searchParams.get('open') === '1');
  useRouteMeta();
  const [activeTab, setActiveTab] = useState(() => {
    // Check URL parameter for tab selection
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      // Validate tab index (0 = Request Credits, 1 = Transaction History, 2 = Request History)
      // Note: Tab order depends on user permissions - some tabs may not be visible
      if (tabIndex >= 0 && tabIndex <= 2) {
        return tabIndex;
      }
    }
    return 0; // Default to Request Tokens / v2 marketplace (wireframe tab 0)
  });
  const [v2Plans, setV2Plans] = useState([]);
  const [v2Addons, setV2Addons] = useState([]);
  const [v2ActiveSub, setV2ActiveSub]         = useState(null); // v2 subscription — used to detect current plan
  const [v2Balance, setV2Balance]             = useState(null); // v2 token balance (null = not loaded yet)
  const [v2Initial, setV2Initial]             = useState(null); // v2 total initial tokens
  const [v2Used, setV2Used]                   = useState(null); // v2 total tokens used
  const [v2RecentlyExpired, setV2RecentlyExpired] = useState(null); // sub expired within 90d
  const [v2HasUsedFreePlan, setV2HasUsedFreePlan] = useState(false); // merchant ever used free plan
  const [v2CarryForward, setV2CarryForward]   = useState(0);   // tokens eligible for carry-forward
  const [v2BillingCurrency, setV2BillingCurrency] = useState('INR'); // INR for +91, USD for all others
  const [v2YearlyDiscountPct, setV2YearlyDiscountPct] = useState(0); // subv2_config.yearly_billing_discount_pct
  const [v2Loading, setV2Loading] = useState(false);
  const [autopayCancelLoading, setAutopayCancelLoading] = useState(false);
  const [autopayMsg, setAutopayMsg] = useState('');
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  // checkout dialog state — payment flow lives in SubscriptionCheckoutDialog
  const [checkoutItem, setCheckoutItem] = useState(null); // { type:'plan'|'addon', data, pricing, billingPeriod }
  const [transactions, setTransactions] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]); // <-- Add state for requests
  const [customCreditsDialog, setCustomCreditsDialog] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [maxCreditLimit, setMaxCreditLimit] = useState(10000000); // Default fallback for MAX_CREDIT_ALLOCATION_LIMIT
  const [validationError, setValidationError] = useState('');

  // For responsive tabs
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [error, setError] = useState('');
  // Transaction History
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [transactionStartDate, setTransactionStartDate] = useState('');
  const [transactionEndDate, setTransactionEndDate] = useState('');
  // Request History
  const [requestPage, setRequestPage] = useState(1);
  const [requestTotalPages, setRequestTotalPages] = useState(1);
  const [requestStatusFilter, setRequestStatusFilter] = useState('');
  const [requestStartDate, setRequestStartDate] = useState('');
  const [requestEndDate, setRequestEndDate] = useState('');
  const [transactionDateError, setTransactionDateError] = useState('');
  const [requestDateError, setRequestDateError] = useState('');
  const [successDialog, setSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);

  // Contact Sales dialog (enterprise / quote_only plans)
  const [contactDialog, setContactDialog] = useState(false);
  const [contactPlan, setContactPlan] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const { hasPermission, loading: permissionLoading } = useMerchantPermissions();
  const [showWhatsAppSmsAuth, setShowWhatsAppSmsAuth] = useState(true);
  useEffect(() => {
    merchantAuthAPI.checkAuthVisibility()
      .then(r => { if (typeof r.data?.show_whatsapp_sms_auth === 'boolean') setShowWhatsAppSmsAuth(r.data.show_whatsapp_sms_auth); })
      .catch(() => {});
  }, []);

  // Date validation function
  const validateDates = (start, end) => {
    const today = new Date().toISOString().split('T')[0];

    if (start && start > today) {
      return "Start date cannot be in the future";
    }
    if (end && end > today) {
      return "End date cannot be in the future";
    }
    if (start && end && start > end) {
      return "Start date cannot be after end date";
    }
    return '';
  };
  const canRequestTokens = hasPermission('Credits Management', 'request_tokens');
  const canViewTransactions = hasPermission('Credits Management', 'view_transactions');
  const canViewRequestHistory = hasPermission('Credits Management', 'request_history');

  // Reset page to 1 when filter or tab changes
  useEffect(() => {
    setPage(1);
  }, [filter, activeTab, transactionStartDate, transactionEndDate]);
  // Reset requestPage to 1 when requestStatusFilter or tab changes
  useEffect(() => {
    setRequestPage(1);
  }, [requestStatusFilter, activeTab, requestStartDate, requestEndDate]);

  const { merchant, refreshProfile } = useMerchantAuth();
  const navigate = useNavigate();

  // Infinite scroll state for transactions
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [isFetchingMoreTransactions, setIsFetchingMoreTransactions] = useState(false);
  const [showNextTransactionsSkeleton, setShowNextTransactionsSkeleton] = useState(false);
  const transactionsSentinelRef = useRef(null);

  // Infinite scroll state for requests
  const [hasMoreRequests, setHasMoreRequests] = useState(true);
  const [isFetchingMoreRequests, setIsFetchingMoreRequests] = useState(false);
  const [showNextRequestsSkeleton, setShowNextRequestsSkeleton] = useState(false);
  const requestsSentinelRef = useRef(null);

  // If tab 0 is hidden by permissions, fall back to the first visible tab
  useEffect(() => {
    if (permissionLoading) return;
    if (activeTab === 0 && !canRequestTokens) {
      setActiveTab(canViewTransactions ? 1 : canViewRequestHistory ? 2 : 0);
    }
  }, [permissionLoading, activeTab, canRequestTokens, canViewTransactions, canViewRequestHistory]);

  useEffect(() => {
    loadV2Data();
    loadSystemSettings(); // Load system settings for credit limits

    if (activeTab === 1) {
      // Validate transaction dates
      const error = validateDates(transactionStartDate, transactionEndDate);
      setTransactionDateError(error);

      if (error) {
        return; // Don't make API call if validation fails
      }

      // initial load for transactions
      setHasMoreTransactions(true);
      loadTransactions({ append: false, targetPage: 1 });
    }
    if (activeTab === 2) {
      // Validate request dates
      const error = validateDates(requestStartDate, requestEndDate);
      setRequestDateError(error);

      if (error) {
        return; // Don't make API call if validation fails
      }

      // initial load for requests
      setHasMoreRequests(true);
      loadCreditRequests({ append: false, targetPage: 1 });
    }
  }, [activeTab, filter, requestStatusFilter, transactionStartDate, transactionEndDate, requestStartDate, requestEndDate]);

  // Load when page changes due to infinite scroll
  useEffect(() => {
    if (activeTab !== 1) return;
    if (page === 1) return;
    loadTransactions({ append: true, targetPage: page });
  }, [page, activeTab]);

  useEffect(() => {
    if (activeTab !== 2) return;
    if (requestPage === 1) return;
    loadCreditRequests({ append: true, targetPage: requestPage });
  }, [requestPage, activeTab]);

  const loadV2Data = async () => {
    setV2Loading(true);
    try {
      const [plansRes, addonsRes, subRes] = await Promise.all([
        getMerchantPlans(),
        getMerchantAddons(),
        getMerchantSubscription().catch(() => ({ data: { subscription: null } })),
        refreshProfile().catch(() => {}),  // keep merchant.subscription_info in sync
      ]);
      setV2Plans(plansRes.data || []);
      setV2Addons(addonsRes.data || []);
      setV2ActiveSub(subRes.data?.subscription || null);
      setV2RecentlyExpired(subRes.data?.recently_expired || null);
      setV2HasUsedFreePlan(subRes.data?.has_used_free_plan || false);
      setV2CarryForward(subRes.data?.carry_forward_tokens || 0);
      if (subRes.data?.billing_currency) setV2BillingCurrency(subRes.data.billing_currency);
      // Yearly tab badge — driven by subv2_config, not hardcoded.
      if (subRes.data?.yearly_billing_discount_pct != null) {
        setV2YearlyDiscountPct(Number(subRes.data.yearly_billing_discount_pct) || 0);
      }
      if (subRes.data?.token_balance != null) {
        setV2Balance(subRes.data.token_balance);
        setV2Initial(subRes.data.total_initial_tokens ?? null);
        setV2Used(subRes.data.total_tokens_used ?? null);
      }
    } catch (e) {
      console.error('Failed to load v2 plans/addons', e);
      setError('Failed to load subscription data. Please refresh the page.');
    } finally {
      setV2Loading(false);
    }
  };

  // Stop Razorpay recurring billing; plan remains active until expiry_date
  const handleCancelAutopay = async () => {
    setAutopayCancelLoading(true);
    setAutopayMsg('');
    try {
      await cancelAutopay();
      setAutopayMsg('Autopay cancelled. Your plan stays active until the current period ends.');
      loadV2Data();
    } catch (e) {
      setAutopayMsg(e.response?.data?.error || 'Failed to cancel autopay');
    } finally {
      setAutopayCancelLoading(false);
    }
  };

  const openContactDialog = (plan) => {
    setContactPlan(plan);
    setContactMessage(
      `Hi, I'm interested in the ${plan.display_name} enterprise plan. Please contact me with more details and pricing.`
    );
    setContactError('');
    setContactSuccess(false);
    setContactDialog(true);
  };

  const closeContactDialog = () => {
    setContactDialog(false);
    setContactPlan(null);
    setContactSuccess(false);
    setContactError('');
    setContactMessage('');
  };

  const handleContactSubmit = async () => {
    if (!contactMessage.trim()) {
      setContactError('Please enter a message.');
      return;
    }
    setContactLoading(true);
    setContactError('');
    try {
      await merchantAPI.contactUs({
        name: merchant?.name || '',
        email: merchant?.email || '',
        mobile: merchant?.mobile || '',
        mobile_code: merchant?.mobile_code || '+91',
        message: contactMessage.trim(),
      });
      setContactSuccess(true);
    } catch (e) {
      setContactError(e.response?.data?.error || 'Failed to send enquiry. Please try again.');
    } finally {
      setContactLoading(false);
    }
  };

  const openPlanCheckout = (plan, period) => {
    if (!showWhatsAppSmsAuth) return;
    const version      = plan.published_version;
    const allPricing   = (version?.pricing || []).filter(p => p.is_period_active !== false);

    // Priority: merchant's active-sub currency > their billing currency (from mobile_code) > first available
    const activeCurr   = v2ActiveSub?.currency_code || v2BillingCurrency;
    const currencyPool = allPricing.filter(p => p.currency_code === activeCurr);
    const pool         = currencyPool.length ? currencyPool : allPricing;

    const pricing = pool.find(p => p.billing_period === period) || pool[0];
    if (!pricing) return; // plan has no pricing at all — don't open checkout
    setCheckoutItem({ type: 'plan', data: plan, pricing, billingPeriod: pricing.billing_period });
  };

  const openAddonCheckout = (addon) => {
    if (!showWhatsAppSmsAuth) return;
    if (!v2ActiveSub || v2ActiveSub.status === 'grace' || addon.is_eligible_for_current_plan === false) return;
    // Token packs are re-purchasable; only feature addons are one-time
    if (addon.addon_category !== 'token_pack' && addon.is_owned === true) return;
    const pricing = (addon.pricing || [])[0];
    if (!pricing) return;
    setCheckoutItem({ type: 'addon', data: addon, pricing, billingPeriod: pricing?.billing_cycle });
  };

  const loadTransactions = async (opts = { append: false, targetPage: null }) => {
    const perPage = 10;
    const pageToLoad = (opts.targetPage !== null && opts.targetPage !== undefined) ? opts.targetPage : page;
    if (opts.append) {
      setIsFetchingMoreTransactions(true);
    } else {
      setLoading(true);
    }
    try {
      const params = {
        page: pageToLoad,
        per_page: perPage,
        ...(filter && { type: filter }),
        ...(transactionStartDate && { start_date: transactionStartDate }),
        ...(transactionEndDate && { end_date: transactionEndDate })
      };

      const response = await merchantAPI.getCreditTransactions(params);
      const items = response.data.transactions || [];
      const pages = response.data.pagination?.pages || 1;
      setTotalPages(pages);
      const hasMore = pageToLoad < pages;
      setHasMoreTransactions(hasMore);
      if (opts.append) {
        setTransactions(prev => {
          const prevIds = new Set(prev.map(t => t.id));
          const merged = [...prev];
          for (const it of items) {
            if (!prevIds.has(it.id)) merged.push(it);
          }
          return merged;
        });
      } else {
        setTransactions(items);
      }
    } catch (error) {
      console.error('Failed to load transactions:', error);
      setError('Failed to load transaction history');
    } finally {
      if (opts.append) {
        setIsFetchingMoreTransactions(false);
        setShowNextTransactionsSkeleton(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Load system settings for credit limits on component mount
  // This fetches the MAX_CREDIT_ALLOCATION_LIMIT from the backend for validation
  const loadSystemSettings = async () => {
    try {
      const response = await masterDataConfigAPI.getPublicSystemSettings();
      const settings = response.data.settings;
      if (settings.MAX_CREDIT_ALLOCATION_LIMIT) {
        setMaxCreditLimit(settings.MAX_CREDIT_ALLOCATION_LIMIT);
      }
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
      // Keep default value if fetch fails
    }
  };

  const loadCreditRequests = async (opts = { append: false, targetPage: null }) => {
    const perPage = 10;
    const pageToLoad = (opts.targetPage !== null && opts.targetPage !== undefined) ? opts.targetPage : requestPage;
    if (opts.append) {
      setIsFetchingMoreRequests(true);
    } else {
      setLoading(true);
    }
    try {
      const params = {
        page: pageToLoad,
        per_page: perPage,
        ...(requestStatusFilter && { status: requestStatusFilter }),
        ...(requestStartDate && { start_date: requestStartDate }),
        ...(requestEndDate && { end_date: requestEndDate })
      };

      const response = await merchantAPI.getCreditRequests(params);
      const items = response.data.requests || [];
      const pages = response.data.pagination?.pages || 1;
      setRequestTotalPages(pages);
      const hasMore = pageToLoad < pages;
      setHasMoreRequests(hasMore);
      if (opts.append) {
        setCreditRequests(prev => {
          const prevIds = new Set(prev.map(r => r.id));
          const merged = [...prev];
          for (const it of items) {
            if (!prevIds.has(it.id)) merged.push(it);
          }
          return merged;
        });
      } else {
        setCreditRequests(items);
      }
      // Optionally handle pagination if needed
    } catch (error) {
      console.error('Failed to load credit requests:', error);
      setError('Failed to load token requests');
    } finally {
      if (opts.append) {
        setIsFetchingMoreRequests(false);
        setShowNextRequestsSkeleton(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Intersection Observer for transactions
  useEffect(() => {
    if (activeTab !== 1) return;
    const node = transactionsSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMoreTransactions && !isFetchingMoreTransactions && !loading && !showNextTransactionsSkeleton) {
        setShowNextTransactionsSkeleton(true);
        setTimeout(() => {
          setPage(prev => prev + 1);
        }, 2000);
      }
    }, { root: null, rootMargin: '200px', threshold: 0.0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab, hasMoreTransactions, isFetchingMoreTransactions, loading, showNextTransactionsSkeleton]);

  // Intersection Observer for requests
  useEffect(() => {
    if (activeTab !== 2) return;
    const node = requestsSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMoreRequests && !isFetchingMoreRequests && !loading && !showNextRequestsSkeleton) {
        setShowNextRequestsSkeleton(true);
        setTimeout(() => {
          setRequestPage(prev => prev + 1);
        }, 2000);
      }
    }, { root: null, rootMargin: '200px', threshold: 0.0 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [activeTab, hasMoreRequests, isFetchingMoreRequests, loading, showNextRequestsSkeleton]);

  // Trim to first page when scrolled to very top
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY <= 0) {
        if (activeTab === 1) {
          setTransactions(prev => prev.slice(0, 10));
          setPage(1);
          setHasMoreTransactions(totalPages > 1);
          setShowNextTransactionsSkeleton(false);
        } else if (activeTab === 2) {
          setCreditRequests(prev => prev.slice(0, 10));
          setRequestPage(1);
          setHasMoreRequests(requestTotalPages > 1);
          setShowNextRequestsSkeleton(false);
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeTab, totalPages, requestTotalPages]);

  // Validation function for credit amount - ensures input meets minimum and maximum requirements
  const validateCreditAmount = (amount) => {
    const numAmount = parseInt(amount);
    if (!numAmount || numAmount < 100) {
      return `Minimum custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount is 100`;
    }
    if (numAmount > maxCreditLimit) {
      return `Maximum allowed is ${maxCreditLimit.toLocaleString()} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}`;
    }
    return null;
  };

  const handleCustomCredits = async () => {
    const valErr = validateCreditAmount(customAmount);
    if (valErr) {
      setValidationError(valErr);
      return;
    }

    try {
      setLoading(true);
      const response = await merchantAPI.requestCustomCredits({
        amount: parseInt(customAmount),
        notes: customNotes
      });

      setCustomCreditsDialog(false);
      setCustomAmount('');
      setCustomNotes('');
      setValidationError('');

      // Show beautiful success dialog
      setSuccessMessage(`Custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request submitted successfully!`);
      setRequestDetails({
        requestId: response.data?.request_id,
        status: response.data?.status || 'PENDING',
        instructions: 'Your request will be reviewed by our team and you will be contacted within 24-48 hours.',
        credits: customAmount,
        packageName: `Custom ${FlutterServicesAPI.getCreditLabel()}`
      });
      setSuccessDialog(true);

    } catch (error) {
      console.error('Custom credit request failed:', error);
      setError(`Failed to submit custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialog(false);
    setSuccessMessage('');
    setRequestDetails(null);
  };

  const handleCancelCustomCredits = () => {
    setCustomCreditsDialog(false);
    setCustomAmount('');
    setCustomNotes('');
    setError('');
    setValidationError('');
  };

  const getTransactionIcon = (type, credits) => {
    // For admin adjustments, determine icon based on credit amount
    if (type === 'ADMIN_ADJUSTMENT' || type === 'ADMIN_CREDIT_ADJUSTMENT') {
      if (credits > 0) return <Add sx={{ color: '#16a34a' }} />;
      if (credits < 0) return <Remove color="error" />;
      return <Info sx={{ color: '#64748b' }} />; // Neutral icon for credits == 0
    }

    switch (type) {
      case 'CREDIT': return <Add sx={{ color: '#16a34a' }} />;
      case 'DEBIT': return <Remove color="error" />;
      case 'REFUND': return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.27768 9.15682L4.63884 9.79566L4 9.15682L4.63884 8.51798L5.27768 9.15682ZM9.79566 20C9.55601 20 9.32618 19.9048 9.15672 19.7353C8.98727 19.5659 8.89207 19.336 8.89207 19.0964C8.89207 18.8567 8.98727 18.6269 9.15672 18.4574C9.32618 18.288 9.55601 18.1928 9.79566 18.1928V20ZM9.15682 14.3136L4.63884 9.79566L5.91653 8.51798L10.4345 13.036L9.15682 14.3136ZM4.63884 8.51798L9.15682 4L10.4345 5.27768L5.91653 9.79566L4.63884 8.51798ZM5.27768 8.25322H14.7654V10.0604H5.27768V8.25322ZM14.7654 20H9.79566V18.1928H14.7654V20ZM20.6388 14.1266C20.6388 15.6843 20.02 17.1782 18.9185 18.2797C17.8171 19.3812 16.3232 20 14.7654 20V18.1928C15.2994 18.1928 15.8282 18.0876 16.3215 17.8833C16.8148 17.6789 17.2631 17.3794 17.6407 17.0018C18.0182 16.6242 18.3178 16.176 18.5221 15.6827C18.7264 15.1893 18.8316 14.6606 18.8316 14.1266H20.6388ZM14.7654 8.25322C16.3232 8.25322 17.8171 8.87202 18.9185 9.9735C20.02 11.075 20.6388 12.5689 20.6388 14.1266H18.8316C18.8316 13.5926 18.7264 13.0639 18.5221 12.5705C18.3178 12.0772 18.0182 11.629 17.6407 11.2514C17.2631 10.8738 16.8148 10.5743 16.3215 10.3699C15.8282 10.1656 15.2994 10.0604 14.7654 10.0604V8.25322Z" fill="#16A34A" />
        </svg>
      );
      default: return <Receipt />;
    }
  };

  const getTransactionColor = (type, credits) => {
    // For admin adjustments, determine color based on credit amount
    if (type === 'ADMIN_ADJUSTMENT' || type === 'ADMIN_CREDIT_ADJUSTMENT') {
      if (credits > 0) return 'success';
      if (credits < 0) return 'error';
      return 'default'; // credits == 0 (e.g., subscription extension)
    }

    switch (type) {
      case 'CREDIT': return 'success';
      case 'DEBIT': return 'error';
      case 'REFUND': return 'success';
      default: return 'default';
    }
  };

  // Format date in DD/MM/YYYY format for better readability with leading zeros
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  };

  // Shared v2 marketplace — same Token Packages / Add On UI on desktop and mobile
  const formatV2Price = (amount, currencyCode) => {
    if (amount == null || Number.isNaN(amount)) return '—';
    const locale = currencyCode === 'USD' ? 'en-US' : 'en-IN';
    return Number(amount).toLocaleString(locale);
  };

  // Responsive grid: stretch rows so card CTAs align at the bottom
  const V2_CARD_GRID = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
    gap: { xs: 2.5, sm: 2 },
    width: '100%',
    alignItems: 'stretch',
  };

  // Fixed price block height — keeps "Add-Ons Included" aligned across plan cards
  const V2_PLAN_PRICE_BLOCK = {
    minHeight: { xs: 130, md: 140 },
    my: 1.5,
    pb: 1.5,
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
  };

  // Uniform CTA height so bottom buttons line up across the row
  const V2_PLAN_BTN = { borderRadius: '8px', fontWeight: 700, textTransform: 'none', minHeight: 40 };

  const renderV2Marketplace = () => {
    if (!showWhatsAppSmsAuth) return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Subscription management is not available in the app.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please visit our website to manage your subscription.
        </Typography>
      </Box>
    );
    return (
    <Box sx={{
      width: '100%',
      bgcolor: { xs: mobileCreditsOpen ? 'transparent' : '#fff', md: 'transparent' },
      border: { xs: mobileCreditsOpen ? 'none' : '1px solid #e5e7eb', md: 'none' },
      borderRadius: { xs: mobileCreditsOpen ? 0 : '12px', md: 0 },
      p: { xs: mobileCreditsOpen ? 0 : 2, md: 0 },
      boxShadow: { xs: mobileCreditsOpen ? 'none' : '0 1px 4px rgba(0,0,0,0.08)', md: 'none' },
    }}>
      {/* Grace state banner — plan expired within 90d, carry-forward eligible */}
      {!v2ActiveSub && v2RecentlyExpired && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 2, p: 2, mb: 3 }}>
          <Box sx={{ fontSize: 20, lineHeight: 1, flexShrink: 0 }}>⚠️</Box>
          <Box flex={1}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#92400e', mb: 0.25 }}>
              {v2RecentlyExpired.status === 'cancelled'
                ? <>Your <strong>{v2RecentlyExpired.plan_name}</strong> plan was cancelled on{' '}
                    {new Date(v2RecentlyExpired.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                : <>Your <strong>{v2RecentlyExpired.plan_name}</strong> plan expired on{' '}
                    {new Date(v2RecentlyExpired.expiry_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</>
              }
            </Typography>
            {v2CarryForward > 0 ? (
              <Typography sx={{ fontSize: 13, color: '#78350f' }}>
                You have <strong>{v2CarryForward.toLocaleString('en-IN')} tokens</strong> that will carry forward if you subscribe to a new plan now.
                {' '}These expire 90 days after your new plan starts.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 13, color: '#78350f' }}>
                Subscribe to a plan to restore access to your Glammify features.
              </Typography>
            )}
          </Box>
        </Box>
      )}


      {/* Token Packages header + billing toggle */}
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 1.5, sm: 0 }}
        mb={2.5}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>Token Packages</Typography>
        <Box sx={{ display: 'inline-flex', bgcolor: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: '8px', p: '3px', gap: '2px' }}>
          {['monthly', 'yearly'].map(bp => (
            <Box key={bp} onClick={() => setBillingPeriod(bp)}
              sx={{
                px: 2, py: 0.75, borderRadius: '6px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                transition: 'all .15s',
                bgcolor: billingPeriod === bp ? '#fff' : 'transparent',
                color: billingPeriod === bp ? '#6318C6' : '#6b7280',
                boxShadow: billingPeriod === bp ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
              {bp === 'monthly' ? 'Monthly' : 'Yearly'}
              {bp === 'yearly' && v2YearlyDiscountPct > 0 && (
                <Box component="span" sx={{ display: 'inline-block', bgcolor: '#dcfce7', color: '#16a34a', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.25, borderRadius: '4px', ml: 0.5 }}>
                  {v2YearlyDiscountPct}% extra
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Plans grid — full-width cards on mobile, 4-col on desktop */}
      {v2Loading
        ? <Box py={4} display="flex" justifyContent="center"><CircularProgress size={28} sx={{ color: '#6318C6' }} /></Box>
        : v2Plans.length === 0
          ? <Box py={4} textAlign="center"><Typography color="text.secondary" fontSize={14}>No plans available</Typography></Box>
          : (() => {
              const PLAN_COLORS  = ['#0f6173', '#6318C6', '#e91e8c', '#b45309'];
              const activePlanVersionId  = v2ActiveSub?.plan_version_id;
              const activeBillingPeriod  = v2ActiveSub?.billing_period;
              const activeCurrency       = v2ActiveSub?.currency_code || v2BillingCurrency;
              const activeTier           = v2ActiveSub?.plan_tier ?? -1;
              const hasActivePlan = activePlanVersionId != null;
              // Yearly tab: only show plans that actually have a yearly pricing row
              // for the merchant's active currency.  Always show the merchant's current
              // active plan (so they can see it), and always show the monthly tab as-is.
              const _pricingPool = (plan) => {
                const allP = (plan.published_version?.pricing || []).filter(px => px.is_period_active !== false);
                const currP = allP.filter(px => px.currency_code === activeCurrency);
                return currP.length ? currP : allP;
              };
              const _hasYearlyPricing  = (plan) => _pricingPool(plan).some(px => px.billing_period === 'yearly');
              const _hasMonthlyPricing = (plan) => _pricingPool(plan).some(px => px.billing_period === 'monthly' || px.billing_period === 'one_time');
              const visiblePlans = billingPeriod === 'yearly'
                ? v2Plans.filter(p => {
                    if ((p.plan_tier ?? 0) === 0 || p.plan_code === 'free') return false; // free has no yearly pricing — always hide
                    if (p.published_version?.id === activePlanVersionId) return true; // always show current paid plan
                    return _hasYearlyPricing(p);
                  })
                : v2Plans.filter(p => {
                    if ((p.plan_tier ?? 0) === 0 || p.plan_code === 'free') return true; // free always shows on monthly
                    if (p.published_version?.id === activePlanVersionId) return true; // always show current plan
                    return _hasMonthlyPricing(p);
                  });
              return (
                <Box sx={{ ...V2_CARD_GRID, mb: 3 }}>
                  {visiblePlans.map((plan) => {
                    const version = plan.published_version;
                    const allP    = (version?.pricing || []).filter(p => p.is_period_active !== false);
                    const currencyP = allP.filter(p => p.currency_code === activeCurrency);
                    const pool    = currencyP.length ? currencyP : allP;
                    const pricing  = pool.find(p => p.billing_period === billingPeriod) || pool[0];
                    const monthlyP = pool.find(p => p.billing_period === 'monthly');
                    const yearlyP  = pool.find(p => p.billing_period === 'yearly');
                    const currSym  = (pricing?.currency_code || activeCurrency) === 'USD' ? '$' : '₹';
                    const exclPrice    = (row) => row ? parseFloat(row.discounted_price_excl_tax || row.mrp_excl_tax || 0) : null;
                    const isContact    = plan.sales_model === 'quote_only';
                    const planCurrencies = (version?.pricing || []).map(p => p.currency_code);
                    const sameCurrency   = !activeCurrency || planCurrencies.length === 0
                      || planCurrencies.includes(activeCurrency);
                    const planTier        = plan.plan_tier ?? 0;
                    const color           = PLAN_COLORS[planTier % PLAN_COLORS.length];
                    const isFree          = planTier === 0 || plan.plan_code === 'free';
                    const isSamePlan   = hasActivePlan && version?.id === activePlanVersionId;
                    const isFreeCurrent = !hasActivePlan && !v2RecentlyExpired && !v2HasUsedFreePlan && planTier === 0;
                    // Non-recurring periods (one_time, lifetime, trial) don't map to a tab —
                    // treat them as matching any tab so the card always shows as CURRENT PLAN.
                    const NON_RECURRING = ['one_time', 'lifetime', 'trial'];
                    const isCurrent    = (isSamePlan && (
                      activeBillingPeriod === billingPeriod ||
                      !activeBillingPeriod ||
                      NON_RECURRING.includes(activeBillingPeriod)
                    )) || isFreeCurrent;
                    const isSamePlanDiffPeriod = isSamePlan && !isCurrent;
                    const isYearlyToMonthlySwitch = isSamePlanDiffPeriod && activeBillingPeriod === 'yearly' && billingPeriod === 'monthly';
                    const isCrossCurrency = hasActivePlan && !isSamePlan && !sameCurrency;
                    const isSameTier      = hasActivePlan && !isSamePlan && !isContact && sameCurrency && activeTier >= 0 && planTier === activeTier;
                    const isUpgrade       = hasActivePlan && !isSamePlan && !isContact && sameCurrency && activeTier >= 0 && planTier > activeTier;
                    const isDowngrade     = hasActivePlan && !isSamePlan && !isContact && sameCurrency && activeTier >= 0 && planTier < activeTier;
                    const mPrice  = exclPrice(monthlyP);
                    const yPrice  = exclPrice(yearlyP);
                    const savings = (mPrice && yPrice && mPrice > 0)
                      ? Math.round((1 - yPrice / (mPrice * 12)) * 100)
                      : 0;
                    const tokenMorePct = (monthlyP?.bundled_tokens > 0 && yearlyP?.bundled_tokens > 0)
                      ? Math.round(((yearlyP.bundled_tokens / (monthlyP.bundled_tokens * 12)) - 1) * 100)
                      : 0;
                    const BADGES = [null, null, { label: '♥ Most Popular', bg: '#f43f5e', color: '#fff' }, { label: '★ Best Value', bg: '#f59e0b', color: '#fff' }];
                    const badge  = isCurrent
                      ? { label: '✓ Current Plan', bg: '#6318C6', color: '#fff' }
                      : isSamePlanDiffPeriod
                        ? { label: `Active (${(activeBillingPeriod || '').toLowerCase()})`, bg: '#6b7280', color: '#fff' }
                        : (BADGES[planTier] || null);
                    const cardBorder = isCurrent ? '#6318C6' : isContact ? '#f59e0b' : planTier === 2 ? '#f43f5e' : '#e5e7eb';
                    const cardBg     = isCurrent ? '#fdfaff' : isContact ? '#fffdf7' : '#fff';
                    // Use admin-configured feature labels if set; fall back to RBAC-derived list
                    const planFeatures = (plan.feature_labels?.length
                      ? [...plan.feature_labels]
                          .filter(l => l.status === 1)
                          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                      : (version?.rbac || [])
                          .filter(r => r.is_granted && !r.is_core)
                          .filter((r, i, arr) => arr.findIndex(x => x.module_id === r.module_id) === i));

                    return (
                      <Box
                        key={plan.id}
                        sx={{
                          position: 'relative',
                          pt: '18px',
                          minWidth: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {badge && (
                          <Box position="absolute" top="18px" left="50%"
                            sx={{ transform: 'translate(-50%, -50%)', bgcolor: badge.bg, color: badge.color, fontSize: 11, fontWeight: 600, px: 1.5, py: 0.5, borderRadius: '20px', whiteSpace: 'nowrap', zIndex: 1, display: 'flex', alignItems: 'center', gap: 0.5, boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                            {badge.label}
                          </Box>
                        )}
                        <Box sx={{
                          border: `2px solid ${isContact ? '#F5CA02' : color}`,
                          borderRadius: '16px',
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          bgcolor: cardBg,
                          overflow: 'hidden',
                          transition: 'transform .3s ease, box-shadow .3s ease, border-color .3s ease',
                          '&:hover': {
                            transform: 'scale(1.03)',
                            borderColor: 'rgba(139,92,246,0.35)',
                            boxShadow: '0 18px 40px rgba(99,24,198,0.12)',
                          },
                        }}>
                          {/* Top colored stripe */}
                          <Box sx={{ height: 8, bgcolor: isContact ? '#F5CA02' : color, flexShrink: 0 }} />

                          <Box sx={{ p: '15px 10px 17px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#111827', mb: 2.5, px: 1, lineHeight: 1.2 }}>
                              {plan.display_name}
                            </Typography>

                            {/* Pricing box — fixed minHeight keeps "Add-Ons Included" aligned across all cards */}
                            {isContact ? (
                              <Box sx={{ minHeight: { xs: 100, md: 108 }, mb: 2.5, px: 0.25, display: 'flex', alignItems: 'center' }}>
                                <Box sx={{
                                  background: '#fff',
                                  border: '2px solid transparent',
                                  borderRadius: '10px',
                                  backgroundImage: 'linear-gradient(#fff, #fff), linear-gradient(to right, #6A3CC7 0%, #E0C34A 100%)',
                                  backgroundOrigin: 'border-box',
                                  backgroundClip: 'padding-box, border-box',
                                  width: '100%',
                                  p: '10px 19px',
                                  textAlign: 'center',
                                }}>
                                  <Typography sx={{
                                    fontSize: 28, fontWeight: 600,
                                    background: 'linear-gradient(to right, #6A3CC7 0%, #E0C34A 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                  }}>
                                    Contact Us
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ border: `1px solid ${color}`, borderRadius: '22px', p: '18px 20px 14px', mb: 2.5, textAlign: 'center', bgcolor: '#fff', minHeight: { xs: 100, md: 108 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                {isFree ? (
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: 30, color, lineHeight: 1.1 }}>
                                      Free
                                    </Typography>
                                  </Box>
                                ) : billingPeriod === 'monthly' && monthlyP && mPrice != null ? (
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: 22, color, lineHeight: 1.1, mb: 0.5 }}>
                                      {currSym} {formatV2Price(mPrice, pricing?.currency_code)}/month
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#4b5563', fontWeight: 500, mb: tokenMorePct > 0 ? 1 : 0 }}>
                                      {monthlyP.bundled_tokens > 0
                                        ? `${Number(monthlyP.bundled_tokens).toLocaleString('en-IN')} Tokens`
                                        : '\u00A0'}
                                    </Typography>
                                    {tokenMorePct > 0 && (
                                      <Box sx={{ display: 'inline-block', bgcolor: color, color: '#fff', fontSize: 12, fontWeight: 600, px: 2, py: 0.5, borderRadius: '999px' }}>
                                        {tokenMorePct}% More
                                      </Box>
                                    )}
                                  </Box>
                                ) : yearlyP && yPrice != null ? (
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: 22, color, lineHeight: 1.1, mb: 0.5 }}>
                                      {currSym} {formatV2Price(yPrice, pricing?.currency_code)}/year
                                    </Typography>
                                    <Typography sx={{ fontSize: 14, color: '#4b5563', fontWeight: 500, mb: (savings > 0 || tokenMorePct > 0) ? 1 : 0 }}>
                                      {yearlyP.bundled_tokens > 0
                                        ? `${Number(yearlyP.bundled_tokens).toLocaleString('en-IN')} Tokens`
                                        : '\u00A0'}
                                    </Typography>
                                    {tokenMorePct > 0 && (
                                      <Box sx={{ display: 'inline-block', bgcolor: color, color: '#fff', fontSize: 12, fontWeight: 600, px: 2, py: 0.5, borderRadius: '999px' }}>
                                        {`${tokenMorePct}% More`}
                                      </Box>
                                    )}
                                  </Box>
                                ) : pricing ? (
                                  <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: 22, color, lineHeight: 1.1, mb: 0.5 }}>
                                      {currSym} {formatV2Price(exclPrice(pricing), pricing?.currency_code)}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13, color: '#4b5563', fontWeight: 500, mb: 0.5 }}>
                                      One-time
                                    </Typography>
                                    {pricing.bundled_tokens > 0 && (
                                      <Typography sx={{ fontSize: 14, color: '#4b5563', fontWeight: 500 }}>
                                        {Number(pricing.bundled_tokens).toLocaleString('en-IN')} Tokens
                                      </Typography>
                                    )}
                                  </Box>
                                ) : null}
                              </Box>
                            )}

                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', px: 1 }}>
                              <Typography sx={{
                                fontSize: 14, fontWeight: 700,
                                textTransform: 'capitalize', mb: 1.5,
                                ...(isContact
                                  ? { background: 'linear-gradient(to right, #6A3CC7 0%, #E0C34A 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }
                                  : { color }),
                              }}>
                                Add-Ons Included
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                {planFeatures.length > 0 ? planFeatures.map((r, fi) => (
                                  <Box key={r.id ?? r.module_id ?? fi} display="flex" alignItems="center" gap={1.5} mb={1}>
                                    <Box sx={{ flexShrink: 0, width: 22, height: 22 }}>
                                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="12" cy="12" r="11" fill={isContact ? '#F5CA02' : color} stroke={isContact ? '#F5CA02' : color} strokeWidth="1.5" />
                                        <path d="M7 12.5L10.2 15.5L17 8.5" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </Box>
                                    <Typography sx={{ fontSize: 13, color: '#363636', fontWeight: 600, lineHeight: 1.4 }}>
                                      {r.label || MODULE_DISPLAY_NAMES[r.module_name] || r.module_name}
                                    </Typography>
                                  </Box>
                                )) : (
                                  <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>—</Typography>
                                )}
                              </Box>
                            </Box>

                            <Box mt="auto" pt={1.5}>
                              {isCurrent ? (
                                <Button fullWidth disabled
                                  sx={{ ...V2_PLAN_BTN, fontSize: 13, bgcolor: '#ede9fe', color: '#6318C6', '&.Mui-disabled': { bgcolor: '#ede9fe', color: '#6318C6' } }}>
                                  Active
                                </Button>
                              ) : (isFree && v2HasUsedFreePlan) ? (
                                <Button fullWidth disabled
                                  sx={{ ...V2_PLAN_BTN, fontSize: 13, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                  Trial Used
                                </Button>
                              ) : isYearlyToMonthlySwitch ? (
                                <Button fullWidth disabled
                                  title="You're on a yearly plan. Switch to monthly only at renewal."
                                  sx={{ ...V2_PLAN_BTN, fontSize: 12, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                  Monthly at Renewal
                                </Button>
                              ) : isSamePlanDiffPeriod ? (
                                (billingPeriod === 'yearly' ? yearlyP : monthlyP) ? (
                                  <Button fullWidth variant="contained"
                                    onClick={() => openPlanCheckout(plan, billingPeriod)}
                                    sx={{ ...V2_PLAN_BTN, fontSize: 13, bgcolor: color, '&:hover': { filter: 'brightness(0.88)' } }}>
                                    Switch to {billingPeriod === 'yearly' ? 'Yearly' : 'Monthly'}
                                  </Button>
                                ) : (
                                  <Button fullWidth disabled
                                    sx={{ ...V2_PLAN_BTN, fontSize: 12, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                    No {billingPeriod} plan
                                  </Button>
                                )
                              ) : isContact ? (
                                <Button fullWidth variant="outlined"
                                  onClick={() => openContactDialog(plan)}
                                  sx={{ ...V2_PLAN_BTN, fontSize: 13, borderColor: '#F5CA02', color: '#b45309', borderWidth: '1.5px', '&:hover': { bgcolor: '#fffbeb', borderColor: '#d97706' } }}>
                                  Contact Sales
                                </Button>
                              ) : isCrossCurrency ? (
                                <Button fullWidth disabled
                                  title="This plan is in a different currency. Switch after your current plan expires."
                                  sx={{ ...V2_PLAN_BTN, fontSize: 12, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                  Different Currency
                                </Button>
                              ) : isSameTier ? (
                                <Button fullWidth disabled
                                  sx={{ ...V2_PLAN_BTN, fontSize: 12, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                  Same Tier
                                </Button>
                              ) : isDowngrade ? (
                                <Button fullWidth disabled
                                  sx={{ ...V2_PLAN_BTN, fontSize: 12, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                                  Switch at Renewal
                                </Button>
                              ) : isUpgrade ? (
                                <Button fullWidth variant="contained"
                                  onClick={() => openPlanCheckout(plan, billingPeriod)}
                                  sx={{ ...V2_PLAN_BTN, fontSize: 13, bgcolor: color, '&:hover': { filter: 'brightness(0.88)' } }}>
                                  Upgrade
                                </Button>
                              ) : (
                                <Button fullWidth variant="outlined"
                                  onClick={() => openPlanCheckout(plan, billingPeriod)}
                                  sx={{ ...V2_PLAN_BTN, fontSize: 13, borderColor: color, color, borderWidth: '1.5px', '&:hover': { bgcolor: `${color}10`, borderColor: color } }}>
                                  Subscribe
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()
      }

      {v2Addons.length > 0 && (
        <Box sx={{ border: 'none', borderTop: '2px solid #e5e7eb', my: 3.5 }} />
      )}

      {v2Addons.length > 0 && (
        <Box>
          {v2Addons.filter(a => a.addon_category === 'token_pack').length > 0 && (
            <Box mb={3}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', mb: 2 }}>Add On List</Typography>
              <Box sx={V2_CARD_GRID}>
                {v2Addons.filter(a => a.addon_category === 'token_pack').map(addon => {
                  const pricing = (addon.pricing || [])[0];
                  const mrp    = parseFloat(pricing?.mrp_excl_tax || pricing?.price || 0);
                  const disc   = parseFloat(pricing?.discounted_price_excl_tax || pricing?.price || 0);
                  const tokens = parseInt(pricing?.tokens_grant || 0);
                  const pct    = mrp && disc && mrp !== disc ? Math.round((1 - disc / mrp) * 100) : 0;
                  const isGrace  = v2ActiveSub?.status === 'grace';
                  const isLocked = !v2ActiveSub || isGrace || addon.is_eligible_for_current_plan === false;
                  const currSym = (pricing?.currency_code || v2BillingCurrency) === 'USD' ? '$' : '₹';
                  return (
                    <Box key={addon.id} sx={{
                      border: '1.5px solid #e5e7eb', borderTop: '3px solid #6318C6',
                      borderRadius: '12px', p: 2.25,
                      display: 'flex', flexDirection: 'column',
                      bgcolor: '#fff', minWidth: 0, width: '100%', height: '100%',
                      opacity: isLocked ? 0.6 : 1,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'box-shadow .15s',
                      '&:hover': isLocked ? {} : { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
                    }}>
                      <Box sx={{ minHeight: 52, mb: 0.5 }}>
                        {isLocked && (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#f3f4f6', color: '#6b7280', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', mb: 1, width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {!v2ActiveSub ? '🔒 Active plan required' : isGrace ? '🔒 Renew plan to buy' : '🔒 Requires Plan'}
                          </Box>
                        )}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#f3eeff', color: '#6318C6', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          ⚡ Token Pack
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5, minHeight: 20 }}>{addon.display_name}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1.5, lineHeight: 1.5, minHeight: 36 }}>
                        {addon.description || '\u00A0'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px', mb: 1.5, p: '10px 12px', bgcolor: '#f3eeff', borderRadius: '8px', minHeight: 52 }}>
                        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#6318C6' }}>+{tokens.toLocaleString('en-IN')}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#6318C6', fontWeight: 600 }}>Tokens</Typography>
                      </Box>
                      <Box mt="auto" pt={1.5} sx={{ borderTop: '1px solid #e5e7eb' }}>
                        {pct > 0 && (
                          <Typography sx={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through', mb: 0.25 }}>
                            {currSym} {formatV2Price(mrp, pricing?.currency_code)}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="baseline" flexWrap="wrap" gap={0.75} mb={1.5}>
                          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
                            {currSym} {formatV2Price(disc, pricing?.currency_code)}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>one-time</Typography>
                          {pct > 0 && (
                            <Box component="span" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: '4px' }}>
                              {pct}% off
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: '#9ca3af', mb: 1.5 }}>+ GST · shown at checkout</Typography>
                        <Button fullWidth variant="contained" size="small"
                          disabled={isLocked}
                          onClick={() => openAddonCheckout(addon)}
                          sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', bgcolor: '#6318C6', py: 1.1, '&:hover': { bgcolor: '#5215a8' }, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                          {isLocked ? (!v2ActiveSub ? 'Subscribe first' : isGrace ? 'Renew plan to buy' : 'Upgrade to buy') : 'Buy Now'}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {v2Addons.filter(a => a.addon_category !== 'token_pack').length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#111827', mb: 2 }}>Feature Add On</Typography>
              <Box sx={V2_CARD_GRID}>
                {v2Addons.filter(a => a.addon_category !== 'token_pack').map(addon => {
                  const pricing  = (addon.pricing || [])[0];
                  const mrp      = parseFloat(pricing?.mrp_excl_tax || pricing?.price || 0);
                  const disc     = parseFloat(pricing?.discounted_price_excl_tax || pricing?.price || 0);
                  const pct      = mrp && disc && mrp !== disc ? Math.round((1 - disc / mrp) * 100) : 0;
                  const isOwned         = addon.is_owned === true;
                  const lockReason      = addon.lock_reason;
                  const isIncluded      = lockReason === 'included_in_plan';
                  const isGraceLocked   = !isOwned && !isIncluded && v2ActiveSub?.status === 'grace';
                  const isUpgradeNeeded = !isOwned && !isIncluded && !isGraceLocked && addon.is_eligible_for_current_plan === false;
                  const isApiAddon = (addon.addon_code || '').toLowerCase().includes('api')
                    || (addon.display_name || '').toLowerCase().includes('api');
                  const isPremiumShots = (addon.addon_code || '').toLowerCase().includes('premium')
                    || (addon.display_name || '').toLowerCase().includes('premium');
                  const upgradeTarget   = addon.requires_plan_id
                    ? v2Plans.find(p => p.id === addon.requires_plan_id)
                    : null;
                  const upgradeLabel    = upgradeTarget?.display_name || 'a higher plan';
                  const cardStyle = (isOwned || isIncluded)
                    ? { border: '1.5px solid #16a34a', borderTop: '3px solid #16a34a', bgcolor: '#f0fdf4' }
                    : (isGraceLocked || isUpgradeNeeded)
                      ? { border: '1.5px solid #e5e7eb', borderTop: '3px solid #0ea5e9', bgcolor: '#fff', opacity: 0.6 }
                      : { border: '1.5px solid #e5e7eb', borderTop: '3px solid #0ea5e9', bgcolor: '#fff' };
                  const currSym = (pricing?.currency_code || v2BillingCurrency) === 'USD' ? '$' : '₹';
                  const addonFeatures = addon.feature_labels?.length
                    ? [...addon.feature_labels]
                        .filter(l => l.status === 1)
                        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                    : (addon.rbac || [])
                        .filter(r => r.is_granted)
                        .filter((r, i, arr) => arr.findIndex(x => x.module_id === r.module_id) === i);
                  return (
                    <Box key={addon.id} sx={{
                      ...cardStyle,
                      borderRadius: '12px', p: 2.25,
                      display: 'flex', flexDirection: 'column',
                      minWidth: 0, width: '100%', height: '100%',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      transition: 'box-shadow .15s',
                      '&:hover': (isUpgradeNeeded || isGraceLocked) ? {} : { boxShadow: '0 4px 16px rgba(0,0,0,0.10)' },
                    }}>
                      <Box sx={{ minHeight: 32, mb: 0.5 }}>
                      {(isOwned || isIncluded)
                        ? (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', bgcolor: '#dcfce7', color: '#15803d', fontSize: 10, fontWeight: 700, px: 1.25, py: 0.5, borderRadius: '20px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {isOwned ? '✓ Active in your account' : '✓ Included in your plan'}
                          </Box>
                        )
                        : isGraceLocked
                          ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#f3f4f6', color: '#6b7280', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              🔒 Renew plan to buy
                            </Box>
                          )
                        : isUpgradeNeeded
                          ? (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', bgcolor: '#f3f4f6', color: '#6b7280', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                              🔒 Requires {upgradeLabel}
                            </Box>
                          )
                          : isApiAddon
                            ? (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#f3f4f6', color: '#6b7280', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                Add-On
                              </Box>
                            )
                            : (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: '#e0f2fe', color: '#0369a1', fontSize: 10, fontWeight: 700, px: 1, py: 0.3, borderRadius: '4px', width: 'fit-content', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                ✦ Feature
                              </Box>
                            )
                      }
                      </Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#111827', mb: 0.5, minHeight: 20 }}>{addon.display_name}</Typography>
                      <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1.25, lineHeight: 1.5, minHeight: 36 }}>
                        {addon.description || '\u00A0'}
                      </Typography>
                      <Box sx={{ flex: 1, mb: 1.5 }}>
                        {addonFeatures.map((r, ai) => (
                          <Box key={r.id ?? r.module_id ?? ai} display="flex" alignItems="flex-start" gap={0.75} mb={0.5}>
                            <Box sx={{ width: 15, height: 15, borderRadius: '50%', bgcolor: '#e0f2fe', color: '#0369a1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, flexShrink: 0, mt: '1px' }}>✓</Box>
                            <Typography sx={{ fontSize: 12, color: '#111827', lineHeight: 1.4 }}>{r.label || MODULE_DISPLAY_NAMES[r.module_name] || r.module_name}</Typography>
                          </Box>
                        ))}
                      </Box>
                      <Box mt="auto" pt={1.5} sx={{ borderTop: '1px solid #e5e7eb' }}>
                        {pct > 0 && (
                          <Typography sx={{ fontSize: 11, color: '#9ca3af', textDecoration: 'line-through', mb: 0.25 }}>
                            {currSym} {formatV2Price(mrp, pricing?.currency_code)}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="baseline" flexWrap="wrap" gap={0.75} mb={1.5}>
                          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
                            {currSym} {formatV2Price(disc, pricing?.currency_code)}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
                            {pricing?.billing_cycle === 'monthly' ? '/ month' : pricing?.billing_cycle === 'yearly' ? '/ year' : 'one-time'}
                          </Typography>
                          {pct > 0 && (
                            <Box component="span" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: '4px' }}>
                              {pct}% off
                            </Box>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: '#9ca3af', mb: 1.5 }}>+ GST · shown at checkout</Typography>
                        {(isOwned || isIncluded) ? (
                          <Button fullWidth disabled
                            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', py: 1.1, bgcolor: '#ede9fe', color: '#6318C6', '&.Mui-disabled': { bgcolor: '#ede9fe', color: '#6318C6' } }}>
                            {isOwned ? 'Active' : 'Included'}
                          </Button>
                        ) : isGraceLocked ? (
                          <Button fullWidth disabled
                            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', py: 1.1, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                            Renew plan to buy
                          </Button>
                        ) : isUpgradeNeeded ? (
                          <Button fullWidth disabled
                            sx={{ borderRadius: '8px', fontWeight: 700, textTransform: 'none', py: 1.1, '&.Mui-disabled': { bgcolor: '#f3f4f6', color: '#9ca3af' } }}>
                            Upgrade to {upgradeLabel}
                          </Button>
                        ) : (
                          <Button fullWidth
                            variant={isPremiumShots ? 'contained' : 'outlined'}
                            size="small"
                            onClick={() => openAddonCheckout(addon)}
                            sx={{
                              borderRadius: '8px', fontWeight: 700, textTransform: 'none', py: 1.1,
                              ...(isPremiumShots
                                ? { bgcolor: '#6318C6', '&:hover': { bgcolor: '#5215a8' } }
                                : { borderColor: '#6318C6', color: '#6318C6', borderWidth: '1.5px', '&:hover': { bgcolor: '#f3eeff', borderColor: '#6318C6' } }),
                            }}>
                            Buy Now
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

        </Box>
      )}
    </Box>
    );
  };

  return (
    <>
      <DynamicMerchantTabs />
      <Container id="credits-management-container" maxWidth={false} sx={{ mb: 4, px: '0 !important', py: '0 !important' }}>
        {/* Credits Overview after tabs */}
        <Grid container spacing={3} sx={{ mb: 3, mt: 0, px: 0, py: 0, display: { xs: mobileCreditsOpen ? 'none' : 'flex', md: 'flex' } }}>
          {/* Current Balance */}
          <Grid item xs={12} sm={6} md={4} sx={{ height: '100%', mt: 0, pt: '0 !important', mb: { xs: 2, sm: 0 } }}>
            <Card elevation={0} sx={{
              height: { xs: 110, sm: 115 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(99, 24, 197, 0.15)',
              boxShadow: '0px 4.33px 12.98px rgba(0, 0, 0, 0.08)',
              borderRadius: '16.31px',
              position: 'relative',
              backgroundColor: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                border: '2px solid #6318C6',
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2, pt: 2, px: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16.31px',
                      color: '#1E2939',
                      fontFamily: 'Montserrat, sans-serif',
                      pr: '75px'
                    }}
                  >
                    {FlutterServicesAPI.getCreditLabel()} Balance
                  </Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: 16, right: 16, width: 65, height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src="/assets/img/Icon-1.svg"
                    alt="Current Balance Icon"
                    style={{ width: '65px', height: '65px' }}
                  />
                </Box>
                <Typography
                  sx={{
                    mb: { xs: 1, sm: 0 },
                    fontWeight: 600,
                    fontSize: '27.62px',
                    color: '#651BC6',
                    fontFamily: 'Montserrat, sans-serif',
                    textAlign: 'left',
                    lineHeight: 1.2
                  }}
                >
                  {v2Balance != null ? formatCredits(v2Balance) : (merchant?.credits_balance ? formatCredits(merchant.credits_balance) : '0')}
                </Typography>

              </CardContent>
            </Card>
          </Grid>

          {/* Total Requested */}
          <Grid item xs={12} sm={6} md={4} sx={{ height: '100%', mt: 0, pt: '0 !important', mb: { xs: 2, sm: 0 } }}>
            <Card elevation={0} sx={{
              height: { xs: 110, sm: 115 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(99, 24, 197, 0.15)',
              boxShadow: '0px 4.33px 12.98px rgba(0, 0, 0, 0.08)',
              borderRadius: '16.31px',
              position: 'relative',
              backgroundColor: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                border: '2px solid #6318C6',
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2, pt: 2, px: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16.31px',
                      color: '#1E2939',
                      fontFamily: 'Montserrat, sans-serif',
                      pr: '75px'
                    }}
                  >
                    Total {FlutterServicesAPI.getCreditLabel()} Requested
                  </Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: 16, right: 16, width: 65, height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src="/assets/img/Icon-4.svg"
                    alt="Total Requested Icon"
                    style={{ width: '65px', height: '65px' }}
                  />
                </Box>
                <Typography
                  sx={{
                    mb: { xs: 1, sm: 0 },
                    fontWeight: 600,
                    fontSize: '27.62px',
                    color: '#651BC6',
                    fontFamily: 'Montserrat, sans-serif',
                    textAlign: 'left',
                    lineHeight: 1.2
                  }}
                >
                  {v2Initial != null ? formatCredits(v2Initial) : (merchant?.total_credits_purchased ? formatCredits(merchant.total_credits_purchased) : '0')}
                </Typography>

              </CardContent>
            </Card>
          </Grid>

          {/* Total Used */}
          <Grid item xs={12} sm={6} md={4} sx={{ height: '100%', mt: 0, pt: '0 !important', mb: { xs: 0, sm: 0 } }}>
            <Card elevation={0} sx={{
              height: { xs: 110, sm: 115 },
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(99, 24, 197, 0.15)',
              boxShadow: '0px 4.33px 12.98px rgba(0, 0, 0, 0.08)',
              borderRadius: '16.31px',
              position: 'relative',
              backgroundColor: 'white',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                border: '2px solid #6318C6',
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 2, pt: 2, px: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16.31px',
                      color: '#1E2939',
                      fontFamily: 'Montserrat, sans-serif',
                      pr: '75px'
                    }}
                  >
                    Total {FlutterServicesAPI.getCreditLabel()} Used
                  </Typography>
                </Box>
                <Box sx={{ position: 'absolute', top: 16, right: 16, width: 65, height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img
                    src="/assets/img/Icon-5.svg"
                    alt="Total Used Icon"
                    style={{ width: '65px', height: '65px' }}
                  />
                </Box>
                <Typography
                  sx={{
                    mb: { xs: 1, sm: 0 },
                    fontWeight: 600,
                    fontSize: '27.62px',
                    color: '#651BC6',
                    fontFamily: 'Montserrat, sans-serif',
                    textAlign: 'left',
                    lineHeight: 1.2
                  }}
                >
                  {v2Used != null ? formatCredits(v2Used) : (merchant?.total_credits_used ? formatCredits(merchant.total_credits_used) : '0')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* 4th Card - Mobile Only: Credits Management expandable card */}
        {!mobileCreditsOpen && (
          <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
            <Box
              onClick={() => setMobileCreditsOpen(true)}
              sx={{
                border: '1px solid #D1D1D1',
                borderRadius: '15.15px',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '15.15px',
                  color: '#1F2937',
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                {FlutterServicesAPI.getCreditLabel()} Management
              </Typography>
              <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img
                  src="/assets/img/icons/carbon_chevron-up-outline.svg"
                  alt="arrow"
                  style={{ width: '24px', height: '24px' }}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Mobile Inline Panel for Credits Management */}
        {mobileCreditsOpen && (
          <Box
            sx={{
              display: { xs: 'block', md: 'none' },
              mx: -0,
            }}
          >
            {/* Back row */}
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} px={{ xs: 0.5, sm: 0 }}>
              <Button onClick={() => setMobileCreditsOpen(false)}
                startIcon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                    <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
                sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}>
                Back
              </Button>
              <Typography variant="h6" sx={{ color: '#1E2939', fontFamily: 'var(--font-montserrat-semibold)', fontWeight: 600, fontSize: '17.59px' }}>{FlutterServicesAPI.getCreditLabel()} Management</Typography>
            </Box>
            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                pb: 1,
              }}
            >
              <Button
                onClick={() => setMobileCreditsOpen(false)}
                startIcon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="13" viewBox="0 0 8 13" fill="none">
                    <path d="M6.41309 0.916079L0.916291 6.41287L6.41309 11.9097" stroke="#651BC6" strokeWidth="1.83226" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
                sx={{ color: '#4A5565', px: 0, textTransform: 'none', fontFamily: 'var(--font-montserrat-medium)', fontWeight: 500, fontSize: '17.59px', '&:hover': { background: 'transparent', opacity: 0.8 } }}
              >
                Back
              </Button>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '17.59px',
                  color: '#1E2939',
                  fontFamily: 'var(--font-montserrat-semibold)',
                }}
              >
                {FlutterServicesAPI.getCreditLabel()} Management
              </Typography>
            </Box> */}

            {/* Panel Content */}
            <Box sx={{ pt: 0 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Styled Scrollable Tabs - matching image */}
              <Box sx={{
                mb: 2.5, overflowX: 'auto', whiteSpace: 'nowrap', pb: 0.5,
                '&::-webkit-scrollbar': { display: 'none' },
                'scrollbarWidth': 'none',
              }}>
                <Box sx={{ display: 'inline-flex', gap: 1 }}>
                  {canRequestTokens && (
                    <Box
                      onClick={() => setActiveTab(0)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '40px',
                        px: 2.5,
                        borderRadius: '13.2px',
                        border: activeTab === 0 ? '1.5px solid #651BC6' : '1.5px solid #D1D1D1',
                        backgroundColor: activeTab === 0 ? '#651BC6' : '#ffffff',
                        color: activeTab === 0 ? '#fff' : '#111827',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: activeTab === 0 ? 500 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                        boxShadow: activeTab === 0 ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
                      }}
                    >
                      Request {FlutterServicesAPI.getCreditLabel()}
                    </Box>
                  )}
                  {canViewTransactions && (
                    <Box
                      onClick={() => setActiveTab(1)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '40px',
                        px: 2.5,
                        borderRadius: '13.2px',
                        border: activeTab === 1 ? '1.5px solid #651BC6' : '1.5px solid #D1D1D1',
                        backgroundColor: activeTab === 1 ? '#651BC6' : '#ffffff',
                        color: activeTab === 1 ? '#fff' : '#111827',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: activeTab === 1 ? 500 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                        boxShadow: activeTab === 1 ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
                      }}
                    >
                      Transaction History
                    </Box>
                  )}
                  {canViewRequestHistory && (
                    <Box
                      onClick={() => setActiveTab(2)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '40px',
                        px: 2.5,
                        borderRadius: '13.2px',
                        border: activeTab === 2 ? '1.5px solid #651BC6' : '1.5px solid #D1D1D1',
                        backgroundColor: activeTab === 2 ? '#651BC6' : '#ffffff',
                        color: activeTab === 2 ? '#fff' : '#111827',
                        fontFamily: 'Montserrat, sans-serif',
                        fontWeight: activeTab === 2 ? 500 : 500,
                        fontSize: '14px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        userSelect: 'none',
                        boxShadow: activeTab === 2 ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
                      }}
                    >
                      Purchase History
                    </Box>
                  )}
                </Box>
              </Box>

              {(loading || v2Loading) && <LinearProgress sx={{ mb: 2 }} />}

              {/* Request Credits Tab - Mobile Panel — same v2 marketplace as desktop */}
              {activeTab === 0 && renderV2Marketplace()}


              {/* Transaction History - Mobile Panel */}
              {activeTab === 1 && (
                <Box>
                  {/* Filter by Type */}
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    <select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      style={{
                        width: '100%',
                        height: '44px',
                        paddingLeft: '16px',
                        paddingRight: '40px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                        color: filter ? '#1F2937' : '#9CA3AF',
                        outline: 'none',
                        backgroundColor: '#fff',
                        boxSizing: 'border-box',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="">All Transactions</option>
                      <option value="SUBSCRIPTION_START">Subscription Start</option>
                      <option value="SUBSCRIPTION_RENEWAL">Subscription Renewal</option>
                      <option value="SUBSCRIPTION_UPGRADE">Subscription Upgrade</option>
                      <option value="SUBSCRIPTION_DOWNGRADE">Subscription Downgrade</option>
                      <option value="SUBSCRIPTION_CANCELLED">Subscription Cancelled</option>
                      <option value="CREDIT_CARRY_FORWARD">Token Carry Forward</option>
                      <option value="TOKEN_TOPUP">Token Pack Purchase</option>
                      <option value="CREDIT">Token Added</option>
                      <option value="DEBIT">Tokens Used</option>
                      <option value="ADMIN_ADJUSTMENT">Admin Adjustment</option>
                      <option value="REFUND">Refund</option>
                    </select>
                    {/* Chevron icon */}
                    <Box sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                  </Box>

                  {/* Date Range */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '14px', color: '#364153', mb: 2 }}>
                      Date Range
                    </Typography>
                    {transactionDateError && (
                      <Typography sx={{ color: '#ef4444', fontSize: 12, mb: 1 }}>{transactionDateError}</Typography>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box component="label" sx={{ position: 'absolute', padding: '2px 5px', borderRadius: '50px', top: '-9px', left: '16px', fontSize: '10px', border: '1px solid #8B8B8B', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                            From
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={transactionStartDate ? dayjs(transactionStartDate) : null}
                            maxDate={dayjs()}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setTransactionStartDate(dateStr);
                              setTransactionDateError(validateDates(dateStr, transactionEndDate));
                            }}
                            slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box component="label" sx={{ position: 'absolute', padding: '2px 5px', borderRadius: '50px', top: '-9px', left: '16px', fontSize: '10px', border: '1px solid #8B8B8B', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                            To
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={transactionEndDate ? dayjs(transactionEndDate) : null}
                            maxDate={dayjs()}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setTransactionEndDate(dateStr);
                              setTransactionDateError(validateDates(transactionStartDate, dateStr));
                            }}
                            slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                          />
                        </Box>
                      </Box>
                    </LocalizationProvider>
                    {(filter || transactionStartDate || transactionEndDate) && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          onClick={() => { setFilter(''); setTransactionStartDate(''); setTransactionEndDate(''); setTransactionDateError(''); }}
                          sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '13px', color: '#7C3AED', cursor: 'pointer', display: 'inline-block' }}
                        >
                          Clear All
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Transaction Cards */}
                  {transactions.map((transaction) => {
                    const isCancelled = transaction.transaction_type === 'SUBSCRIPTION_CANCELLED';
                    const isDebit = transaction.transaction_type === 'DEBIT' ||
                      (transaction.transaction_type === 'ADMIN_ADJUSTMENT' && transaction.credits < 0);
                    const typeColor = isDebit ? '#D32F2F' : isCancelled ? '#9ca3af' : '#10B981';
                    const statusBgColor =
                      (transaction.status === 'COMPLETED' || !transaction.status) ? '#F0FDF4'
                        : (transaction.status === 'PROCESSING' || transaction.status === 'PENDING') ? '#dbeafe'
                          : transaction.status === 'FAILED' ? '#fee2e2'
                            : transaction.status === 'CANCELLED' ? '#ede7f6'
                              : '#e0e0e0';
                    const statusTextColor =
                      (transaction.status === 'COMPLETED' || !transaction.status) ? '#10B981'
                        : (transaction.status === 'PROCESSING' || transaction.status === 'PENDING') ? '#1d4ed8'
                          : transaction.status === 'FAILED' ? '#D32F2F'
                            : transaction.status === 'CANCELLED' ? '#4527a0'
                              : '#333';
                    const typeLabel = getTypeLabel(transaction.transaction_type);
                    return (
                      <Card key={transaction.id} elevation={0} sx={{ mb: 2, width: '100%', borderRadius: '17.37px', border: 'none', boxShadow: '0px 2.17px 8.68px 0px #00000014', p: 2, backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '14.48px', color: typeColor, lineHeight: 1.3 }}>{typeLabel}</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '17.54px', color: typeColor, lineHeight: 1.3 }}>{isDebit ? '-' : '+'}{formatCredits(Math.abs(transaction.credits))}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939' }}>Description</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', textAlign: 'right', ml: 2, flexShrink: 0 }}>{formatDate(transaction.created_at)}</Typography>
                        </Box>
                        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>{transaction.description || 'N/A'}</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Balance After</Typography>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939' }}>{transaction.balance_after ?? 'N/A'}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Status</Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '23px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px' }}>
                                {(transaction.status || 'COMPLETED').toUpperCase()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })}
                  {transactions.length === 0 && !loading && <Box textAlign="center" py={4}><Typography variant="h6" color="text.secondary">No transactions found</Typography></Box>}
                  <Box ref={transactionsSentinelRef} sx={{ height: 1 }} />
                </Box>
              )}


              {/* Request History - Mobile Panel */}
              {activeTab === 2 && (
                <Box>
                  {/* Status Filter */}
                  <Box sx={{ mb: 2, position: 'relative' }}>
                    <select
                      value={requestStatusFilter}
                      onChange={(e) => setRequestStatusFilter(e.target.value)}
                      style={{
                        width: '100%',
                        height: '44px',
                        paddingLeft: '16px',
                        paddingRight: '40px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                        color: requestStatusFilter ? '#1F2937' : '#9CA3AF',
                        outline: 'none',
                        backgroundColor: '#fff',
                        boxSizing: 'border-box',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="" style={{ color: '#9CA3AF' }}>All Statuses</option>
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                    <Box sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Box>
                  </Box>

                  {/* Date Range */}
                  <Box sx={{ mb: 3 }}>
                    <Typography sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '14px', color: '#364153', mb: 2 }}>
                      Date Range
                    </Typography>
                    {requestDateError && (
                      <Typography sx={{ color: '#ef4444', fontSize: 12, mb: 1 }}>{requestDateError}</Typography>
                    )}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box component="label" sx={{ position: 'absolute', padding: '2px 5px', borderRadius: '50px', top: '-9px', left: '16px', fontSize: '10px', border: '1px solid #8B8B8B', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                            From
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={requestStartDate ? dayjs(requestStartDate) : null}
                            maxDate={dayjs()}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setRequestStartDate(dateStr);
                              setRequestDateError(validateDates(dateStr, requestEndDate));
                            }}
                            slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                          />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 130, position: 'relative' }}>
                          <Box component="label" sx={{ position: 'absolute', padding: '2px 5px', borderRadius: '50px', top: '-9px', left: '16px', fontSize: '10px', border: '1px solid #8B8B8B', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                            To
                          </Box>
                          <DatePicker format="DD/MM/YYYY"
                            value={requestEndDate ? dayjs(requestEndDate) : null}
                            maxDate={dayjs()}
                            onChange={(newValue) => {
                              const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                              setRequestEndDate(dateStr);
                              setRequestDateError(validateDates(requestStartDate, dateStr));
                            }}
                            slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                          />
                        </Box>
                      </Box>
                    </LocalizationProvider>
                    {(requestStatusFilter || requestStartDate || requestEndDate) && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          onClick={() => { setRequestStatusFilter(''); setRequestStartDate(''); setRequestEndDate(''); setRequestDateError(''); }}
                          sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '13px', color: '#7C3AED', cursor: 'pointer', display: 'inline-block' }}
                        >
                          Clear All
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Request Cards */}
                  {creditRequests.length === 0 && !loading
                    ? <Box textAlign="center" py={4}><Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>No token requests found</Typography></Box>
                    : creditRequests.filter(req => !requestStatusFilter || req.status === requestStatusFilter).map((req) => {
                      const statusBgColor = req.status === 'APPROVED' ? '#F0FDF4' : req.status === 'REJECTED' ? '#FEE2E2' : req.status === 'PENDING' ? '#FEF3C7' : '#e0e0e0';
                      const statusTextColor = req.status === 'APPROVED' ? '#10B981' : req.status === 'REJECTED' ? '#D32F2F' : req.status === 'PENDING' ? '#d97706' : '#333';
                      const creditsColor = req.status === 'APPROVED' ? '#10B981' : req.status === 'REJECTED' ? '#D32F2F' : '#1E2939';
                      return (
                        <Card key={req.id} elevation={0} sx={{ mb: 2, width: '100%', borderRadius: '17.37px', border: 'none', boxShadow: '0px 2.17px 8.68px 0px #00000014', p: 2, backgroundColor: '#fff' }}>
                          {/* Row 1: Requested Credits (left) + Date (right) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '14.48px', color: creditsColor, lineHeight: 1.3 }}>
                              {formatCredits(req.requested_credits)}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', textAlign: 'right', ml: 2, flexShrink: 0 }}>
                              {formatDate(req.created_at)}
                            </Typography>
                          </Box>
                          {/* Row 2: Reason label + content */}
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Reason</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>{req.request_reason || '-'}</Typography>
                          {/* Row 3: Admin Notes label + content */}
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Admin Notes</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>{req.admin_notes || '-'}</Typography>
                          {/* Row 4: Status (right) */}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Status</Typography>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '23px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px' }}>
                                  {req.status}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      );
                    })
                  }
                  <Box ref={requestsSentinelRef} sx={{ height: 1 }} />
                </Box>
              )}

            </Box>
          </Box>
        )}

        <Paper elevation={3} sx={{
          p: { xs: 0, sm: 2, md: 4 },
          border: { xs: 'none', sm: '1px solid #e5e7eb', md: '1px solid #e5e7eb' },
          boxShadow: 'none',
          backgroundColor: { xs: 'transparent', sm: 'white', md: 'white' },
          borderRadius: '8px',
          display: { xs: 'none', md: 'block' }
        }}>
          <Typography
            variant="h6"
            fontWeight={600}
            fontSize={{ xs: "18px", sm: "20px", md: "27px" }}
            mb={2}
            display="flex"
            alignItems="center"
            gap={1}
          >
            {FlutterServicesAPI.getCreditLabel()} Management
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Tabs - Desktop only */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={(() => {
                // MUI Tabs uses rendered position (0, 1, 2…), not our logical indices.
                // Recompute the MUI index from the current logical activeTab so the
                // correct tab is highlighted when some tabs are permission-hidden.
                const visible = [
                  canRequestTokens     ? 0 : -1,
                  canViewTransactions  ? 1 : -1,
                  canViewRequestHistory ? 2 : -1,
                ].filter(t => t >= 0);
                const muiIdx = visible.indexOf(activeTab);
                return muiIdx >= 0 ? muiIdx : 0;
              })()}
              onChange={(e, v) => {
                const visible = [
                  canRequestTokens     ? 0 : -1,
                  canViewTransactions  ? 1 : -1,
                  canViewRequestHistory ? 2 : -1,
                ].filter(t => t >= 0);
                setActiveTab(visible[v] ?? 0);
              }}
              variant="standard"
              sx={{
                '& .MuiTabs-flexContainer': {
                  paddingLeft: 0
                },
                '& .MuiTab-root:first-of-type': {
                  marginLeft: 0,
                  paddingLeft: 0
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#6318C6'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#6318C6'
                }
              }}
            >
              {canRequestTokens && <Tab label={`Request ${FlutterServicesAPI.getCreditLabel()}`} sx={{ textTransform: 'capitalize' }} />}
              {canViewTransactions && <Tab label="Transaction History" sx={{ textTransform: 'capitalize' }} />}
              {canViewRequestHistory && <Tab label="Purchase History" sx={{ textTransform: 'capitalize' }} />}
            </Tabs>
          </Box>

          {(loading || v2Loading) && <LinearProgress sx={{ mb: 2 }} />}

          {/* ── Request Tokens Tab ── */}
          {activeTab === 0 && renderV2Marketplace()}

          {/* Transaction History Tab */}
          {activeTab === 1 && (
            <Box>
              {/* Filter row: select + date pickers — one line on desktop, stacked on mobile */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: { md: 'center' },
                mb: 3,
              }}>
                {/* Filter by Type select */}
                <Box sx={{ position: 'relative', flex: { md: '0 0 360px' }, width: { xs: '100%', md: '360px' } }}>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    style={{
                      width: '100%',
                      height: '44px',
                      paddingLeft: '16px',
                      paddingRight: '40px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                      color: filter ? '#1F2937' : '#9CA3AF',
                      outline: 'none',
                      backgroundColor: '#fff',
                      boxSizing: 'border-box',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">All Transactions</option>
                    <option value="SUBSCRIPTION_START">Subscription Start</option>
                    <option value="SUBSCRIPTION_RENEWAL">Subscription Renewal</option>
                    <option value="SUBSCRIPTION_UPGRADE">Subscription Upgrade</option>
                    <option value="SUBSCRIPTION_DOWNGRADE">Subscription Downgrade</option>
                    <option value="SUBSCRIPTION_CANCELLED">Subscription Cancelled</option>
                    <option value="CREDIT_CARRY_FORWARD">Token Carry Forward</option>
                    <option value="TOKEN_TOPUP">Token Pack Purchase</option>
                    <option value="CREDIT">Feature / Other Credit</option>
                    <option value="DEBIT">Tokens Used</option>
                    <option value="ADMIN_ADJUSTMENT">Admin Adjustment</option>
                    <option value="REFUND">Refund</option>
                  </select>
                  <Box sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>
                </Box>

                {/* Date Range heading — mobile only */}
                <Typography sx={{
                  display: { xs: 'block', md: 'none' },
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                  fontWeight: 600, fontSize: '14px', color: '#364153',
                }}>
                  Date Range
                </Typography>

                {transactionDateError && (
                  <Typography sx={{ color: '#ef4444', fontSize: 12 }}>{transactionDateError}</Typography>
                )}

                {/* From + To DatePickers */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: { xs: 1, md: '0 0 200px' }, width: { xs: '100%', md: '200px' }, position: 'relative' }}>
                      <Box
                        component="label"
                        sx={{
                          position: 'absolute',
                          top: '-4px',
                          left: '16px',
                          fontSize: '11px',
                          color: '#6B7280',
                          bgcolor: '#fff',
                          px: 0.5,
                          fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                          zIndex: 2,
                          lineHeight: 1,
                        }}
                      >
                        From
                      </Box>
                      <DatePicker format="DD/MM/YYYY"
                        value={transactionStartDate ? dayjs(transactionStartDate) : null}
                        maxDate={dayjs()}
                        onChange={(newValue) => {
                          const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                          setTransactionStartDate(dateStr);
                          setTransactionDateError(validateDates(dateStr, transactionEndDate));
                        }}
                        slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: 1, md: '0 0 200px' }, width: { xs: '100%', md: '200px' }, position: 'relative' }}>
                      <Box
                        component="label"
                        sx={{
                          position: 'absolute',
                          top: '-4px',
                          left: '16px',
                          fontSize: '11px',
                          color: '#6B7280',
                          bgcolor: '#fff',
                          px: 0.5,
                          fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                          zIndex: 2,
                          lineHeight: 1,
                        }}
                      >
                        To
                      </Box>
                      <DatePicker format="DD/MM/YYYY"
                        value={transactionEndDate ? dayjs(transactionEndDate) : null}
                        maxDate={dayjs()}
                        onChange={(newValue) => {
                          const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                          setTransactionEndDate(dateStr);
                          setTransactionDateError(validateDates(transactionStartDate, dateStr));
                        }}
                        slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>

                {(filter || transactionStartDate || transactionEndDate) && (
                  <Typography
                    onClick={() => { setFilter(''); setTransactionStartDate(''); setTransactionEndDate(''); setTransactionDateError(''); }}
                    sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '13px', color: '#7C3AED', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'center' }}
                  >
                    Clear All
                  </Typography>
                )}
              </Box>

              {/* Transaction Cards — desktop table-like header */}
              {transactions.length > 0 && (
                <Box sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  px: '24px',
                  py: '20px',
                  mb: 1,
                  borderRadius: '12px',
                  backgroundColor: '#F3F0FA',
                  gap: 0,
                }}>
                  <Box sx={{ flex: '0 0 130px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 110px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Balance After</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{FlutterServicesAPI.getCreditLabel()}</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</Typography>
                  </Box>
                </Box>
              )}

              {/* Transaction Cards — unified for all screen sizes */}
              <Box>
                {/* Helper: get type color */}
                {transactions.map((transaction) => {
                  const isCancelled = transaction.transaction_type === 'SUBSCRIPTION_CANCELLED';
                  const isDebit     = transaction.transaction_type === 'DEBIT' ||
                    (transaction.transaction_type === 'ADMIN_ADJUSTMENT' && transaction.credits < 0);

                  // Per spec: credit=#10B981, debit=#D32F2F, cancelled=#9ca3af
                  const typeColor = isDebit ? '#D32F2F' : isCancelled ? '#9ca3af' : '#10B981';

                  // Status badge color
                  const statusBgColor =
                    (transaction.status === 'COMPLETED' || !transaction.status) ? '#F0FDF4'
                      : (transaction.status === 'PROCESSING' || transaction.status === 'PENDING') ? '#dbeafe'
                        : transaction.status === 'FAILED' ? '#fee2e2'
                          : transaction.status === 'CANCELLED' ? '#ede7f6'
                            : '#e0e0e0';
                  const statusTextColor =
                    (transaction.status === 'COMPLETED' || !transaction.status) ? '#10B981'
                      : (transaction.status === 'PROCESSING' || transaction.status === 'PENDING') ? '#1d4ed8'
                        : transaction.status === 'FAILED' ? '#D32F2F'
                          : transaction.status === 'CANCELLED' ? '#4527a0'
                            : '#333';

                  const typeLabel = getTypeLabel(transaction.transaction_type);

                  return (
                    <Card
                      key={transaction.id}
                      elevation={0}
                      sx={{
                        mb: 1.5,
                        width: '100%',
                        borderRadius: '17.37px',
                        border: 'none',
                        boxShadow: '0px 2.17px 8.68px 0px #00000014',
                        backgroundColor: '#fff',
                        // Mobile: keep card padding
                        // Desktop: switch to flex row
                      }}
                    >
                      {/* ===== MOBILE layout (xs only) ===== */}
                      <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                        {/* Row 1: Type (left) + Credits (right) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '14.48px', color: typeColor, lineHeight: 1.3 }}>
                            {typeLabel}
                          </Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '17.54px', color: typeColor, lineHeight: 1.3 }}>
                            {isDebit ? '-' : '+'}{formatCredits(Math.abs(transaction.credits))}
                          </Typography>
                        </Box>
                        {/* Row 2: Description label (left) + Date (right) */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939' }}>Description</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', textAlign: 'right', ml: 2, flexShrink: 0 }}>
                            {formatDate(transaction.created_at)}
                          </Typography>
                        </Box>
                        {/* Row 3: Description content */}
                        <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>
                          {transaction.description || 'N/A'}
                        </Typography>
                        {/* Row 4: Balance After (left) + Status (right) */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Balance After</Typography>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939' }}>{transaction.balance_after ?? 'N/A'}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Status</Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '23px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px' }}>
                                {(transaction.status || 'COMPLETED').toUpperCase()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>

                      {/* ===== DESKTOP layout (md+) — single row like a table ===== */}
                      <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        px: '24px',
                        py: '16px',
                        gap: 0,
                      }}>
                        {/* Type */}
                        <Box sx={{ flex: '0 0 130px', minWidth: 0 }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: typeColor, lineHeight: 1.3 }}>
                            {typeLabel}
                          </Typography>
                        </Box>
                        {/* Description */}
                        <Box sx={{ flex: 1, minWidth: 0, px: 1, overflow: 'hidden' }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13px', color: '#1E2939', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {transaction.description || 'N/A'}
                          </Typography>
                        </Box>
                        {/* Date */}
                        <Box sx={{ flex: '0 0 110px', minWidth: 0, textAlign: 'center' }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#1E2939' }}>
                            {formatDate(transaction.created_at)}
                          </Typography>
                        </Box>
                        {/* Balance After */}
                        <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#1E2939' }}>
                            {transaction.balance_after ?? 'N/A'}
                          </Typography>
                        </Box>
                        {/* Credits */}
                        <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '14px', color: typeColor }}>
                            {isDebit ? '-' : '+'}{formatCredits(Math.abs(transaction.credits))}
                          </Typography>
                        </Box>
                        {/* Status */}
                        <Box sx={{ flex: '0 0 120px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '26px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                              {(transaction.status || 'COMPLETED').toUpperCase()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}

                {showNextTransactionsSkeleton && (
                  <>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Card key={`txn-skeleton-${idx}`} elevation={0} sx={{
                        mb: 2,
                        borderRadius: '17.37px',
                        border: 'none',
                        boxShadow: '0px 2.17px 8.68px 0px #00000014',
                        p: { xs: 2, md: '20px 24px' },
                        backgroundColor: '#fff',
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Skeleton variant="rounded" width={80} height={22} animation="wave" sx={{ '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                          <Skeleton variant="rounded" width={60} height={22} animation="wave" sx={{ '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        </Box>
                        <Skeleton variant="rounded" width="75%" height={18} animation="wave" sx={{ mb: 1.5, '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Skeleton variant="rounded" width={100} height={18} animation="wave" sx={{ '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                          <Skeleton variant="rounded" width={80} height={23} animation="wave" sx={{ borderRadius: '100px', '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        </Box>
                      </Card>
                    ))}
                  </>
                )}
              </Box>

              {transactions.length === 0 && !loading && (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="text.secondary">
                    No transactions found
                  </Typography>
                </Box>
              )}

              {/* Infinite scroll sentinel (Transactions) */}
              <Box ref={transactionsSentinelRef} sx={{ height: 1 }} />
            </Box>
          )}

          {/* Request History Tab */}
          {activeTab === 2 && (
            <Box>
              {/* Filter row: select + date pickers — one line on desktop, stacked on mobile */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                alignItems: { md: 'center' },
                mb: 3,
              }}>
                {/* Status filter select */}
                <Box sx={{ position: 'relative', flex: { md: '0 0 360px' }, width: { xs: '100%', md: '360px' } }}>
                  <select
                    value={requestStatusFilter}
                    onChange={(e) => setRequestStatusFilter(e.target.value)}
                    style={{
                      width: '100%',
                      height: '44px',
                      paddingLeft: '16px',
                      paddingRight: '40px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '10px',
                      fontSize: '14px',
                      fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)',
                      color: requestStatusFilter ? '#1F2937' : '#9CA3AF',
                      outline: 'none',
                      backgroundColor: '#fff',
                      boxSizing: 'border-box',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="" style={{ color: '#9CA3AF' }}>All Statuses</option>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                  <Box sx={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9L12 15L18 9" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </Box>
                </Box>

                {/* Date Range heading — mobile only */}
                <Typography sx={{
                  display: { xs: 'block', md: 'none' },
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)',
                  fontWeight: 600, fontSize: '14px', color: '#364153',
                }}>
                  Date Range
                </Typography>

                {requestDateError && (
                  <Typography sx={{ color: '#ef4444', fontSize: 12 }}>{requestDateError}</Typography>
                )}

                {/* From + To DatePickers */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: { xs: 1, md: '0 0 200px' }, width: { xs: '100%', md: '200px' }, position: 'relative' }}>
                      <Box component="label" sx={{ position: 'absolute', top: '-4px', left: '16px', fontSize: '11px', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                        From
                      </Box>
                      <DatePicker format="DD/MM/YYYY"
                        value={requestStartDate ? dayjs(requestStartDate) : null}
                        maxDate={dayjs()}
                        onChange={(newValue) => {
                          const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                          setRequestStartDate(dateStr);
                          setRequestDateError(validateDates(dateStr, requestEndDate));
                        }}
                        slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                      />
                    </Box>
                    <Box sx={{ flex: { xs: 1, md: '0 0 200px' }, width: { xs: '100%', md: '200px' }, position: 'relative' }}>
                      <Box component="label" sx={{ position: 'absolute', top: '-4px', left: '16px', fontSize: '11px', color: '#6B7280', bgcolor: '#fff', px: 0.5, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', zIndex: 2, lineHeight: 1 }}>
                        To
                      </Box>
                      <DatePicker format="DD/MM/YYYY"
                        value={requestEndDate ? dayjs(requestEndDate) : null}
                        maxDate={dayjs()}
                        onChange={(newValue) => {
                          const dateStr = newValue ? newValue.format('YYYY-MM-DD') : '';
                          setRequestEndDate(dateStr);
                          setRequestDateError(validateDates(requestStartDate, dateStr));
                        }}
                        slotProps={{ textField: { fullWidth: true, InputProps: { sx: { height: '40px', borderRadius: '100px', fontSize: 12, fontFamily: 'var(--font-montserrat-medium, Montserrat, sans-serif)', color: '#1F2937', backgroundColor: '#fff', '& fieldset': { border: '1.5px solid #D1D5DB' }, '&:hover fieldset': { border: '1.5px solid #9CA3AF' }, '&.Mui-focused fieldset': { border: '1.5px solid #1E293B' }, '& .MuiInputBase-input': { padding: '0 14px', height: '40px', boxSizing: 'border-box' } } } } }}
                      />
                    </Box>
                  </Box>
                </LocalizationProvider>

                {(requestStatusFilter || requestStartDate || requestEndDate) && (
                  <Typography
                    onClick={() => { setRequestStatusFilter(''); setRequestStartDate(''); setRequestEndDate(''); setRequestDateError(''); }}
                    sx={{ fontFamily: 'var(--font-montserrat-semibold, Montserrat, sans-serif)', fontWeight: 600, fontSize: '13px', color: '#7C3AED', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'center' }}
                  >
                    Clear All
                  </Typography>
                )}
              </Box>

              {/* Request Cards — desktop table-like header */}
              {creditRequests.length > 0 && (
                <Box sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  px: '24px',
                  py: '20px',
                  mb: 1,
                  borderRadius: '12px',
                  backgroundColor: '#F3F0FA',
                  gap: 0,
                }}>
                  <Box sx={{ flex: '0 0 130px', minWidth: 0 }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{FlutterServicesAPI.getCreditLabel()}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reason</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Admin Notes</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 110px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</Typography>
                  </Box>
                  <Box sx={{ flex: '0 0 120px', minWidth: 0, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '13px', color: '#6318C6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</Typography>
                  </Box>
                </Box>
              )}

              {/* Request Cards */}
              <Box>
                {creditRequests.length === 0 && !loading
                  ? <Box textAlign="center" py={4}><Typography variant="body1" color="text.secondary" sx={{ fontSize: '14px' }}>No token requests found</Typography></Box>
                  : creditRequests.filter(req => !requestStatusFilter || req.status === requestStatusFilter).map((req) => {
                    const statusBgColor = req.status === 'APPROVED' ? '#F0FDF4' : req.status === 'REJECTED' ? '#FEE2E2' : req.status === 'PENDING' ? '#FEF3C7' : '#e0e0e0';
                    const statusTextColor = req.status === 'APPROVED' ? '#10B981' : req.status === 'REJECTED' ? '#D32F2F' : req.status === 'PENDING' ? '#d97706' : '#333';
                    const creditsColor = req.status === 'APPROVED' ? '#10B981' : req.status === 'REJECTED' ? '#D32F2F' : '#1E2939';
                    return (
                      <Card key={req.id} elevation={0} sx={{ mb: 1.5, width: '100%', borderRadius: '17.37px', border: 'none', boxShadow: '0px 2.17px 8.68px 0px #00000014', backgroundColor: '#fff' }}>
                        {/* ===== MOBILE layout ===== */}
                        <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
                          {/* Row 1: Credits (left) + Date (right) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '14.48px', color: creditsColor, lineHeight: 1.3 }}>
                              {formatCredits(req.requested_credits)}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', textAlign: 'right', ml: 2, flexShrink: 0 }}>
                              {formatDate(req.created_at)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Reason</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>{req.request_reason || '-'}</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Admin Notes</Typography>
                          <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13.2px', color: '#1E2939', mb: 1.5 }}>{req.admin_notes || '-'}</Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13.2px', color: '#1E2939', mb: 0.25 }}>Status</Typography>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '23px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                                <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px' }}>
                                  {req.status}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* ===== DESKTOP layout — single row like a table ===== */}
                        <Box sx={{
                          display: { xs: 'none', md: 'flex' },
                          alignItems: 'center',
                          px: '24px',
                          py: '16px',
                          gap: 0,
                        }}>
                          {/* Credits */}
                          <Box sx={{ flex: '0 0 130px', minWidth: 0 }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: '14px', color: creditsColor }}>
                              {formatCredits(req.requested_credits)}
                            </Typography>
                          </Box>
                          {/* Reason */}
                          <Box sx={{ flex: 1, minWidth: 0, px: 1, overflow: 'hidden' }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13px', color: '#1E2939', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {req.request_reason || '-'}
                            </Typography>
                          </Box>
                          {/* Admin Notes */}
                          <Box sx={{ flex: 1, minWidth: 0, px: 1, overflow: 'hidden' }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '13px', color: '#1E2939', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {req.admin_notes || '-'}
                            </Typography>
                          </Box>
                          {/* Date */}
                          <Box sx={{ flex: '0 0 110px', minWidth: 0, textAlign: 'center' }}>
                            <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 500, fontSize: '13px', color: '#1E2939' }}>
                              {formatDate(req.created_at)}
                            </Typography>
                          </Box>
                          {/* Status */}
                          <Box sx={{ flex: '0 0 110px', minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: '26px', px: 1.5, borderRadius: '100px', bgcolor: statusBgColor }}>
                              <Typography sx={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 600, fontSize: '10px', color: statusTextColor, lineHeight: 1, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                {req.status}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Card>
                    );
                  })
                }

                {showNextRequestsSkeleton && (
                  <>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Card key={`req-skeleton-${idx}`} elevation={0} sx={{ mb: 2, borderRadius: '17.37px', border: 'none', boxShadow: '0px 2.17px 8.68px 0px #00000014', p: { xs: 2, md: '20px 24px' }, backgroundColor: '#fff' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Skeleton variant="rounded" width={80} height={22} animation="wave" sx={{ '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                          <Skeleton variant="rounded" width={80} height={18} animation="wave" sx={{ '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        </Box>
                        <Skeleton variant="rounded" width="75%" height={18} animation="wave" sx={{ mb: 1.5, '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        <Skeleton variant="rounded" width="60%" height={18} animation="wave" sx={{ mb: 1.5, '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Skeleton variant="rounded" width={80} height={23} animation="wave" sx={{ borderRadius: '100px', '&::after': { background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.60) 50%, rgba(255,255,255,0) 100%)', animationDuration: '0.9s' } }} />
                        </Box>
                      </Card>
                    ))}
                  </>
                )}
              </Box>

              {/* Infinite scroll sentinel (Requests) */}
              <Box ref={requestsSentinelRef} sx={{ height: 1 }} />
            </Box>
          )}
        </Paper>
      </Container>

      {/* Enhanced Custom Credits Dialog */}
      <Dialog open={customCreditsDialog} onClose={handleCancelCustomCredits} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {`Request Custom ${FlutterServicesAPI.getCreditLabel()}`}
            <IconButton onClick={handleCancelCustomCredits} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
            {`Request a custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} amount for your business needs. Minimum 100 ${FlutterServicesAPI.getCreditLabel().toLowerCase()}, maximum ${maxCreditLimit.toLocaleString()} ${FlutterServicesAPI.getCreditLabel().toLowerCase()}.`}
          </Typography>
          <TextField
            fullWidth
            label={`${FlutterServicesAPI.getCreditLabel()} Required *`}
            type="number"
            value={customAmount}
            onChange={(e) => {
              const value = e.target.value;
              setCustomAmount(value);

              // Real-time validation
              const error = validateCreditAmount(value);
              setValidationError(error);
              setError(''); // Clear previous errors
            }}
            placeholder={`Enter ${FlutterServicesAPI.getCreditLabel().toLowerCase()} between 100 and ${maxCreditLimit.toLocaleString()}`}
            inputProps={{
              min: 100,
              max: maxCreditLimit
            }}
            error={!!validationError}
            helperText={validationError || `Enter ${FlutterServicesAPI.getCreditLabel().toLowerCase()} between 100 and ${maxCreditLimit.toLocaleString()}`}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Reason/Notes (Optional)"
            multiline
            rows={3}
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder={`Please provide a reason for your custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} request`}
            helperText={`Explain why you need custom ${FlutterServicesAPI.getCreditLabel().toLowerCase()} (business purpose, project details, etc.)`}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6318C6',
                }
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#6318C6',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '13px',
                opacity: 0.7
              }
            }}
          />

          {(validationError || error) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError || error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCancelCustomCredits}
            disabled={loading}
            sx={{
              textTransform: 'none',
              color: 'black'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomCredits}
            variant="contained"
            disabled={loading || !customAmount || !!validationError}
            sx={{
              background: loading || !customAmount || parseInt(customAmount) < 100 ? '#E0E0E0' : '#6318C6',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                background: loading || !customAmount || parseInt(customAmount) < 100 ? '#E0E0E0' : '#6318C6',
              },
              '&:disabled': {
                background: '#E0E0E0',
                color: '#424242'
              }
            }}
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Beautiful Success Dialog */}
      <Dialog
        open={successDialog}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'white',
            color: '#374151', // Changed to dark color for better contrast on white
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
          <IconButton
            onClick={handleCloseSuccessDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#374151', // Changed to dark color
              '&:hover': { background: 'rgba(0,0,0,0.1)' }
            }}
          >
            <Close />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <Box
              sx={{
                background: 'rgba(99, 24, 198, 0.1)', // Changed to light purple background
                borderRadius: '50%',
                p: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: '#4CAF50' }} />
            </Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#1f2937' }}>
              Success!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, textAlign: 'center', color: '#1f2937' }}>
              {successMessage}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            {requestDetails && (
              <Card sx={{ mt: 1, background: 'transparent', color: '#1f2937', boxShadow: 'none' }}>
                <CardContent sx={{ py: 1 }}>
                  <Grid container spacing={1}>
                    {/* Combined package name and credits into one line */}
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <Typography variant="body1" fontWeight={600} sx={{ color: '#1f2937' }}>
                          {`Requested ${FlutterServicesAPI.getCreditLabel()} - ${requestDetails.credits}`}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8, color: '#1f2937' }}>
              You will be notified once your request is processed.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handleCloseSuccessDialog}
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #6318C6 0%, #6318C6 100%)',
              color: 'white',
              fontWeight: 'bold',
              py: 1.6,
              textTransform: 'none',
              borderRadius: 3,
              letterSpacing: '0.5px',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                transform: 'translateY(-1px)',
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af',
                transform: 'none',
              },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── Contact Sales Dialog (enterprise / quote_only plans) ── */}
      <Dialog open={contactDialog} onClose={closeContactDialog} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>Contact Sales</Typography>
            {contactPlan && (
              <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.25 }}>
                Enquiring about <strong style={{ color: '#b45309' }}>{contactPlan.display_name}</strong>
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeContactDialog} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {contactSuccess ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box sx={{ fontSize: 48, mb: 1.5 }}>✅</Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#15803d', mb: 1 }}>
                Enquiry Sent!
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#6b7280' }}>
                Our sales team will reach out to you at <strong>{merchant?.email}</strong> within 24–48 hours.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', px: 2, py: 1.5, mb: 2.5 }}>
                <Typography sx={{ fontSize: 13, color: '#92400e' }}>
                  We'll reach out to you at <strong>{merchant?.email}</strong>
                  {merchant?.mobile ? ` and ${merchant?.mobile_code || ''} ${merchant?.mobile}` : ''}.
                </Typography>
              </Box>

              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#374151', mb: 0.75 }}>
                Your Message
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={contactMessage}
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder="Tell us about your requirements..."
                inputProps={{ maxLength: 1000 }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 14 } }}
              />
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5, textAlign: 'right' }}>
                {contactMessage.length}/1000
              </Typography>

              {contactError && (
                <Alert severity="error" sx={{ mt: 1.5, borderRadius: '8px', fontSize: 13 }}>
                  {contactError}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          {contactSuccess ? (
            <Button fullWidth variant="contained" onClick={closeContactDialog}
              sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', bgcolor: '#6318C6', '&:hover': { bgcolor: '#5215a8' } }}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closeContactDialog} variant="outlined"
                sx={{ borderRadius: '10px', fontWeight: 600, textTransform: 'none', borderColor: '#e5e7eb', color: '#6b7280', flex: 1 }}>
                Cancel
              </Button>
              <Button onClick={handleContactSubmit} variant="contained" disabled={contactLoading}
                sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', bgcolor: '#f59e0b', color: '#fff', flex: 2, '&:hover': { bgcolor: '#d97706' }, '&.Mui-disabled': { bgcolor: '#fde68a' } }}>
                {contactLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send Enquiry'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Checkout Dialog (plans + add-ons / token packs) ── */}
      {showWhatsAppSmsAuth && <SubscriptionCheckoutDialog
        checkoutItem={checkoutItem}
        onCheckoutItemChange={setCheckoutItem}
        billingCurrency={v2BillingCurrency}
        merchant={merchant}
        v2ActiveSub={v2ActiveSub}
        v2Balance={v2Balance}
        onClose={() => setCheckoutItem(null)}
        onComplete={loadV2Data}
      />}

    </>
  );
};

export default CreditsManagement; 